import { Injectable } from '@angular/core';
import posthog from 'posthog-js'
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';

const FEATURE_FLAG_LOOKUP_TIMEOUT_MS = 2000;

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {

  private readonly lookupsForCurrentUser = new Map<string, { userId: number; enabled: Promise<boolean> }>();

  constructor(private sessionProviderService: SessionProviderService) {
  }

  private isEnabledObservable(key: string): Observable<boolean> {

    return new Observable((observer) => {
      if (!environment.config.posthog.enabled) observer.next(false);

      if (this.sessionProviderService.activeSession?.user?.email){
        posthog.setPersonPropertiesForFlags({'email': this.sessionProviderService.activeSession.user.email});
      }

      posthog.onFeatureFlags(() => {
        observer.next(posthog.isFeatureEnabled(key));
      });
    });
  }

  async isEnabled(key: string): Promise<boolean> {
    let timeoutHandle: ReturnType<typeof setTimeout>;

    const disabledAfterTimeout = new Promise<boolean>((resolve) => {
      timeoutHandle = setTimeout(() => resolve(false), FEATURE_FLAG_LOOKUP_TIMEOUT_MS);
    });

    try {
      return await Promise.race([
        firstValueFrom(this.isEnabledObservable(key)),
        disabledAfterTimeout
      ]);
    } finally {
      clearTimeout(timeoutHandle!);
    }
  }

  isEnabledForCurrentUser(key: string): Promise<boolean> {
    const userId = this.sessionProviderService.activeSession?.user?.id;
    const lookup = this.lookupsForCurrentUser.get(key);
    if (lookup && lookup.userId === userId) return lookup.enabled;
    const enabled = this.isEnabled(key);
    this.lookupsForCurrentUser.set(key, { userId, enabled });
    return enabled;
  }

  /**
   * Get a numeric feature flag value with a default fallback
   * Uses getFeatureFlagPayload() to retrieve the actual payload value, not just the variant key
   */
  async getNumberValue(key: string, defaultValue: number): Promise<number> {
    if (!environment.config.posthog.enabled) {
      return defaultValue;
    }

    if (this.sessionProviderService.activeSession?.user?.email) {
      posthog.setPersonPropertiesForFlags({'email': this.sessionProviderService.activeSession.user.email});
    }

    return new Promise((resolve) => {
      posthog.onFeatureFlags(() => {
        const payload = posthog.getFeatureFlagPayload(key);
        const numValue = payload ? Number(payload) : defaultValue;
        resolve(isNaN(numValue) ? defaultValue : numValue);
      });
    });
  }
}
